import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Formula, FormulaExample } from '@/types/formula';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PlusCircle, Trash2, BookOpen, ZoomIn, ZoomOut, Sparkles, Loader2, AlertCircle, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { MeasurementUnit } from '@/types/shapes';
import { getFormulaExamples, createDefaultFormula, validateFormula, convertToLatex } from '@/utils/formulaUtils';
import { useTranslate } from '@/utils/translate';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { convertNaturalLanguageToExpression } from '@/services/openaiService';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import { Alert, AlertDescription } from '@/components/ui/alert';


// Helper: shallow structural equality for variables objects
type VarCfg = { value: number; min: number; max: number; step?: number };
const variablesEqual = (
  a?: Record<string, VarCfg>,
  b?: Record<string, VarCfg>
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) return false;
  }

  for (const k of ak) {
    const x = a[k];
    const y = b[k];
    if (!y) return false;
    if (x.value !== y.value || x.min !== y.min || x.max !== y.max) return false;
    const xs = x.step === undefined ? undefined : x.step;
    const ys = y.step === undefined ? undefined : y.step;
    if (xs !== ys) return false;
  }
  return true;
};

interface FormulaEditorProps {
  formulas: Formula[];
  onAddFormula: (formula: Formula) => void;
  onUpdateFormula: (id: string, updates: Partial<Formula>) => void;
  onDeleteFormula: (id: string) => void;
  _measurementUnit: MeasurementUnit;
  isOpen: boolean;
  selectedFormulaId?: string | null;
  onSelectFormula?: (id: string) => void;
  onNewFormula?: () => void;
  // Notify when a parameter slider (the new sliders under the input, not the old controls) is being dragged
  onParamSliderActiveChange?: (active: boolean) => void;
}

const FormulaEditor: React.FC<FormulaEditorProps> = ({
  formulas,
  onAddFormula,
  onUpdateFormula,
  onDeleteFormula,
  _measurementUnit,
  isOpen,
  selectedFormulaId,
  onSelectFormula,
  onNewFormula,
  onParamSliderActiveChange
}) => {
  const t = useTranslate();
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isNaturalLanguageOpen, setIsNaturalLanguageOpen] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [isScalePopoverOpen, setIsScalePopoverOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
  const _isMobile = useIsMobile();
  const formulaInputRef = useRef<HTMLInputElement>(null);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toLatex = useCallback((expression: string): string => {
    return convertToLatex(expression);
  }, []);

  // Function to find the selected formula
  const findSelectedFormula = (): Formula | undefined => {
    return formulas.find(f => f.id === selectedFormulaId);
  };

  // Determine the independent variable symbol for a given formula
  const getIndependentVar = (f?: Formula): string => {
    if (!f) return 'x';
    return f.independentVar || (f.type === 'parametric' ? 't' : f.type === 'polar' ? 'θ' : 'x');
  };

  // Create a new formula
  const handleCreateFormula = () => {
    // Always create function type formulas
    const newFormula = createDefaultFormula('function');
    // Ensure the expression is set
    if (!newFormula.expression) {
      newFormula.expression = 'x*x';
    }
    console.log('Creating new formula:', newFormula);
    onAddFormula(newFormula);
    if (onSelectFormula) {
      onSelectFormula(newFormula.id);
    }
  };

  // Debounced validation function
  const debouncedValidate = useCallback((formula: Formula) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      const validation = validateFormula(formula);
      if (!validation.isValid) {
        console.error(`Formula validation error: ${validation.error}`);
      }
      setValidationErrors((prev: Record<string, string | null>) => ({
        ...prev,
        [formula.id]: validation.isValid ? null : (validation.error ?? 'Invalid expression')
      }));
    }, 500); // 500ms debounce
  }, []);

  // Update the formula being edited
  const handleUpdateFormula = (key: keyof Formula, value: string | number | boolean | [number, number]) => {
    if (!selectedFormulaId) return;
    
    // Special handling when updating the expression: detect variables and keep existing values
    if (key === 'expression') {
      const current = findSelectedFormula();
      const expr = String(value);
      // Unicode-aware single-letter variable tokens
      const singleLetterMatches = Array.from(expr.matchAll(/(?<![\p{L}0-9_])([\p{L}])(?![\p{L}0-9_])/gu)).map(m => m[1]);
      const unique = new Set<string>(singleLetterMatches);

      // Determine independent symbol from formula or sensible default
      const indep = getIndependentVar(current);
      // Detect implicit adjacency: xA or Ax (or tA / At) - Unicode-aware
      const rightAdj = new RegExp(`(?<![\\p{L}0-9_])${indep}([\\p{L}])(?![\\p{L}0-9_])`, 'gu');
      const leftAdj = new RegExp(`(?<![\\p{L}0-9_])([\\p{L}])${indep}(?![\\p{L}0-9_])`, 'gu');
      let m: RegExpExecArray | null;
      while ((m = rightAdj.exec(expr)) !== null) unique.add(m[1]);
      while ((m = leftAdj.exec(expr)) !== null) unique.add(m[1]);

      const uniqueVars = Array.from(unique).filter((n) => n !== indep);
      const existing = current?.variables || {};
      const updatedVariables: Record<string, { value: number; min: number; max: number; step?: number }> = { ...existing };

      // Add any missing variables with defaults
      uniqueVars.forEach((name) => {
        if (!updatedVariables[name]) {
          updatedVariables[name] = { value: 1, min: -10, max: 10, step: 0.1 };
        }
      });

      // Remove variables that are no longer present in the new expression
      Object.keys(updatedVariables).forEach((name) => {
        if (!uniqueVars.includes(name)) {
          delete updatedVariables[name];
        }
      });

      // Update both expression and variables at once
      onUpdateFormula(selectedFormulaId, { expression: expr, variables: updatedVariables });
      const updatedFormula: Formula = { ...current!, expression: expr, variables: updatedVariables };
      debouncedValidate(updatedFormula);

      return;
    }

    // Update the formula
    onUpdateFormula(selectedFormulaId, { [key]: value });
    
    // If updating the expression or type, validate with debounce
    if (key === 'type') {
      const current = findSelectedFormula();
      if (!current) return;
      const updatedFormula: Formula = {
        ...current,
        type: value as Formula['type']
      };
      
      debouncedValidate(updatedFormula);
    }
  };

  // Auto-detect variables on load or when formulas change (e.g., opening a shared URL)
  useEffect(() => {
    if (!selectedFormulaId) return;
    const current = findSelectedFormula();
    if (!current) return;
    const expr = current.expression || '';
    const indep = getIndependentVar(current);

    // Extract Unicode-aware single-letter tokens and implicit adjacency around the independent symbol
    const baseMatches = Array.from(expr.matchAll(/(?<![\p{L}0-9_])([\p{L}])(?![\p{L}0-9_])/gu)).map(m => m[1]);
    const unique = new Set<string>(baseMatches);
    const rightAdj = new RegExp(`(^|[^\\p{L}0-9_])${indep}([\\p{L}])($|[^\\p{L}0-9_])`, 'gu');
    const leftAdj = new RegExp(`(^|[^\\p{L}0-9_])([\\p{L}])${indep}($|[^\\p{L}0-9_])`, 'gu');
    let m: RegExpExecArray | null;
    while ((m = rightAdj.exec(expr)) !== null) unique.add(m[2]);
    while ((m = leftAdj.exec(expr)) !== null) unique.add(m[2]);

    const detected = Array.from(unique).filter((n) => n !== indep);
    const existing = current.variables || {};
    const updated: Record<string, { value: number; min: number; max: number; step?: number }> = { ...existing };
    // Only add new variables here (no removals in effect to avoid race with typing)
    detected.forEach((name) => {
      if (!updated[name]) {
        updated[name] = { value: 1, min: 0, max: 10, step: 0.1 };
      }
    });

    // Clean up: remove the independent variable from variables if it exists
    if (updated[indep]) {
      delete updated[indep];
    }

    // Shallow compare to avoid loops
    if (!variablesEqual(existing, updated)) {
      onUpdateFormula(selectedFormulaId, { variables: updated });
    }
  }, [formulas, selectedFormulaId]);

  // Clean up variables when independent variable changes
  useEffect(() => {
    if (!selectedFormulaId) return;
    const current = findSelectedFormula();
    if (!current || !current.variables) return;
    
    const independentVar = current.independentVar || 'x';
    const existing = current.variables;
    
    // Remove independent variable from variables if it exists
    if (existing[independentVar]) {
      const updated = { ...existing };
      delete updated[independentVar];
      onUpdateFormula(selectedFormulaId, { variables: updated });
    }
  }, [selectedFormulaId, findSelectedFormula()?.independentVar, onUpdateFormula]);

  // Handle variable slider changes
  const handleVariableChange = (name: string, newValue: number) => {
    if (!selectedFormulaId) return;
    const current = findSelectedFormula();
    if (!current) return;
    const existing = current.variables || {};
    const updatedVariables = {
      ...existing,
      [name]: { ...(existing[name] || { min: 0, max: 10, step: 0.1 }), value: newValue }
    };
    onUpdateFormula(selectedFormulaId, { variables: updatedVariables });
    debouncedValidate({ ...current, variables: updatedVariables });
  };

  // Handle changes to variable range (min/max)
  const handleVariableRangeChange = (name: string, key: 'min' | 'max', raw: number) => {
    if (!selectedFormulaId) return;
    const current = findSelectedFormula();
    if (!current) return;
    const existing = current.variables || {};
    const cfg = existing[name] || { value: 1, min: 0, max: 10, step: 0.1 };
    let newMin = key === 'min' ? raw : cfg.min;
    let newMax = key === 'max' ? raw : cfg.max;
    if (newMin > newMax) {
      if (key === 'min') newMax = newMin; else newMin = newMax;
    }
    const clampedValue = Math.min(Math.max(cfg.value, newMin), newMax);
    const updatedVariables = { ...existing, [name]: { ...cfg, min: newMin, max: newMax, value: clampedValue } };
    onUpdateFormula(selectedFormulaId, { variables: updatedVariables });
    debouncedValidate({ ...current, variables: updatedVariables });
  };

  const handleLoadExample = (example: FormulaExample) => {
    if (selectedFormulaId) {
      // Update existing formula
      onUpdateFormula(selectedFormulaId, {
        expression: example.expression
      });
    } else {
      // Create new formula from example
      const newFormula = createDefaultFormula('function');
      newFormula.expression = example.expression;
      // Ensure the expression is set
      if (!newFormula.expression) {
        newFormula.expression = 'x*x';
      }
      console.log('Loading example formula:', newFormula);
      onAddFormula(newFormula);
      if (onSelectFormula) {
        onSelectFormula(newFormula.id);
      }
    }
    // Close the examples popover after selection
    setExamplesOpen(false);
  };

  // Handle scale factor change
  const handleScaleFactorChange = (value: number[]) => {
    if (selectedFormulaId) {
      // Convert from logarithmic slider value to actual scale factor
      // This gives finer control at lower values
      const actualScaleFactor = Math.pow(10, value[0]);
      onUpdateFormula(selectedFormulaId, { scaleFactor: actualScaleFactor });
    }
  };

  // Handle natural language conversion
  const handleNaturalLanguageConversion = async () => {
    if (!naturalLanguageInput.trim()) {
      toast.error(t('naturalLanguageEmpty'));
      return;
    }

    setIsProcessing(true);
    try {
      const result = await convertNaturalLanguageToExpression(naturalLanguageInput);
      
      if (selectedFormulaId) {
        // Update the current formula
        onUpdateFormula(selectedFormulaId, {
          expression: result.expression
        });
        toast.success(t('naturalLanguageSuccess'), {
          description: result.explanation
        });
      } else {
        // Create a new formula with the generated expression
        const newFormula = {
          ...createDefaultFormula('function'),
          expression: result.expression
        };
        onAddFormula(newFormula);
        if (onSelectFormula) {
          onSelectFormula(newFormula.id);
        }
        toast.success(t('naturalLanguageSuccess'), {
          description: result.explanation
        });
      }
      
      // Close the popover and reset the input
      setIsNaturalLanguageOpen(false);
      setNaturalLanguageInput('');
    } catch (error) {
      console.error('Error converting natural language:', error);
      toast.error(t('naturalLanguageError'));
    } finally {
      setIsProcessing(false);
    }
  };

  // When the component mounts, select the first formula if any exist and none is selected
  useEffect(() => {
    if (formulas.length > 0 && !selectedFormulaId && onSelectFormula) {
      onSelectFormula(formulas[0].id);
    }
  }, [formulas, selectedFormulaId, onSelectFormula]);

  // Validate formulas when they change, but with debounce
  useEffect(() => {
    formulas.forEach(formula => {
      debouncedValidate(formula);
    });

    // Cleanup timeouts on unmount
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [formulas, debouncedValidate]);

  // Organize examples by category
  const examplesByCategory = getFormulaExamples().reduce((acc, example) => {
    if (!acc[example.category]) {
      acc[example.category] = [];
    }
    acc[example.category].push(example);
    return acc;
  }, {} as Record<string, FormulaExample[]>);

  if (!isOpen) {
    return null;
  }

  // Get the current scale factor
  const currentScaleFactor = findSelectedFormula()?.scaleFactor || 1.0;
  
  return (
    <div 
      className="w-full px-1 sm:px-2" 
      id="formula-editor"
      data-testid="formula-editor"
    >
      <div 
        className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full" 
        id="formula-editor-toolbar"
      >
        {/* Formula Input Section - Full Width */}
        <div className="flex-grow w-full sm:w-auto">
          <div className="flex items-center gap-2">
            {/* Function Signature Input */}
            <div className="flex items-center gap-1 bg-gray-50 border rounded px-2 py-1.5 text-sm">
              <Input
                type="text"
                placeholder="f"
                value={findSelectedFormula()?.functionName || 'f'}
                onChange={(e) => handleUpdateFormula('functionName', e.target.value)}
                className="w-8 h-6 p-0 text-center border-0 bg-transparent text-sm font-medium"
              />
              <span>(</span>
              <Input
                type="text"
                placeholder="x"
                value={findSelectedFormula()?.independentVar || 'x'}
                onChange={(e) => handleUpdateFormula('independentVar', e.target.value)}
                className="w-8 h-6 p-0 text-center border-0 bg-transparent text-sm font-medium"
              />
              <span>) =</span>
            </div>
            
            {/* Expression Input */}
            <Input
              ref={formulaInputRef}
              id="formula-expression-input"
              data-testid="formula-expression-input"
              type="text"
              placeholder={t('enterFormula')}
              value={findSelectedFormula()?.expression || ''}
              onChange={(e) => handleUpdateFormula('expression', e.target.value)}
              className="flex-1 sm:flex-none sm:w-[40%] font-mono"
            />

            {/* Live LaTeX preview */}
            <div className="hidden sm:inline-flex sm:shrink-0 bg-blue-50 border border-blue-200 rounded px-2 py-1 whitespace-pre-wrap min-h-[36px] max-w-[50%]">
              {(() => {
                const selectedFormula = findSelectedFormula();
                const expression = selectedFormula?.expression || '';
                if (!expression) return <InlineMath math="f(x) = " />;
                
                const functionName = selectedFormula?.functionName || 'f';
                const variable = selectedFormula?.independentVar || 'x';
                return <InlineMath math={`${functionName}(${variable}) = ${toLatex(expression)}`} />;
              })()}
            </div>

            {/* Validation error in the remaining space (desktop) */}
            <div className="hidden sm:flex sm:flex-1">
              {selectedFormulaId && validationErrors[selectedFormulaId] && (
                <Alert variant="destructive" className="p-2 w-full">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {validationErrors[selectedFormulaId]}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
          {/* Validation error below on mobile */}
          <div className="sm:hidden mt-2">
            {selectedFormulaId && validationErrors[selectedFormulaId] && (
              <Alert variant="destructive" className="p-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {validationErrors[selectedFormulaId]}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Controls Section - Row on Mobile, Buttons on Desktop */}
        <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
          {/* Function Navigation Buttons - Only show if multiple formulas exist */}
          {formulas.length > 1 && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => {
                        const currentIndex = formulas.findIndex((f) => f.id === selectedFormulaId);
                        const prevIndex = currentIndex === 0 ? formulas.length - 1 : currentIndex - 1;
                        onSelectFormula?.(formulas[prevIndex].id);
                      }}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('previousFormula')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => {
                        const currentIndex = formulas.findIndex((f) => f.id === selectedFormulaId);
                        const nextIndex = currentIndex === formulas.length - 1 ? 0 : currentIndex + 1;
                        onSelectFormula?.(formulas[nextIndex].id);
                      }}
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('nextFormula')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {/* Natural Language Input */}
          <Popover open={isNaturalLanguageOpen} onOpenChange={setIsNaturalLanguageOpen}>
            <TooltipProvider>
              <Tooltip>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="h-9 w-9"
                    >
                      <Sparkles className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                </PopoverTrigger>
                <TooltipContent>
                  <p>{t('naturalLanguageTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-96">
              <div className="space-y-2">
                <Label>{t('naturalLanguageTitle')}</Label>
                <Textarea
                  placeholder={t('naturalLanguagePlaceholder')}
                  value={naturalLanguageInput}
                  onChange={(e) => setNaturalLanguageInput(e.target.value)}
                />
                <Button 
                  onClick={handleNaturalLanguageConversion}
                  disabled={isProcessing}
                  className="w-full"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('naturalLanguageGenerate')
                  )}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Examples */}
          <Popover open={examplesOpen} onOpenChange={setExamplesOpen}>
            <TooltipProvider>
              <Tooltip>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="h-9 w-9"
                    >
                      <BookOpen className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                </PopoverTrigger>
                <TooltipContent>
                  <p>{t('browseExamples')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-96 p-2">
              <div className="grid gap-2">
                {Object.entries(examplesByCategory).map(([category, examples]) => (
                  <div key={category} className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground">
                      {t(`categories.${category}`)}
                    </Label>
                    <div className="grid gap-0.5 mt-1">
                      {examples.map((example) => (
                        <Button
                          key={example.expression}
                          variant="ghost"
                          className="w-full justify-start text-left h-auto py-1"
                          onClick={() => handleLoadExample(example)}
                        >
                          <InlineMath math={convertToLatex(example.expression)} />
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Scale Factor Control */}
          <Popover open={isScalePopoverOpen} onOpenChange={setIsScalePopoverOpen}>
            <TooltipProvider>
              <Tooltip>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="h-9 w-9"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                </PopoverTrigger>
                <TooltipContent>
                  <p>{t('scaleFactorHint')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-96 p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('scaleFactor')}</span>
                  <span className="text-xs text-muted-foreground">
                    {currentScaleFactor < 0.01 
                      ? currentScaleFactor.toFixed(3) 
                      : currentScaleFactor.toFixed(2)}x
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <ZoomOut size={16} className="text-muted-foreground" />
                  <Slider
                    defaultValue={[Math.log10(currentScaleFactor)]}
                    min={-3} // 10^-3 = 0.001
                    max={1}  // 10^1 = 10
                    step={0.01}
                    onValueChange={handleScaleFactorChange}
                    className="flex-1"
                  />
                  <ZoomIn size={16} className="text-muted-foreground" />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0.001x</span>
                  <span>1.0x</span>
                  <span>10x</span>
                </div>

              </div>
            </PopoverContent>
          </Popover>

          {/* Color Picker */}
          <div className="relative">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative">
                    <input
                      type="color"
                      className="opacity-0 absolute inset-0 w-9 h-9 cursor-pointer"
                      value={findSelectedFormula()?.color || '#000000'}
                      onChange={(e) => handleUpdateFormula('color', e.target.value)}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 pointer-events-none"
                    >
                      <div 
                        className="w-4 h-4 rounded-sm"
                        style={{ 
                          backgroundColor: findSelectedFormula()?.color || '#000000',
                        }}
                      />
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('colorPicker')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Delete Formula Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => selectedFormulaId && onDeleteFormula(selectedFormulaId)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('deleteFormula')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Add Formula Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id="add-formula-button"
                  variant="outline"
                  size="sm"
                  className="mt-1 sm:mt-0"
                  data-testid="add-formula-button"
                  onClick={onNewFormula || handleCreateFormula}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('newFormula')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Variable sliders below the input */}
      {findSelectedFormula()?.variables && (
        (() => {
          const current = findSelectedFormula()!;
          const indep = getIndependentVar(current);
          const entries = Object.entries(current.variables || {}).filter(([name]) => name !== indep);
          if (entries.length === 0) return null;
          const hasMultiple = entries.length > 1;
          return (
            <div className="mt-3 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" id="formula-variables">
              {entries.map(([name, cfg]) => (
                <div key={name} className="grid grid-cols-[auto,5rem,1fr,5rem,3rem] items-center gap-2">
                  <span className="text-sm font-medium" aria-hidden>
                    Parameter <InlineMath math={name} />
                  </span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    aria-label={`${name} min`}
                    className="w-20 h-8 px-2 py-1 text-xs"
                    defaultValue={cfg.min}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) handleVariableRangeChange(name, 'min', val);
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) handleVariableRangeChange(name, 'min', val);
                      else e.currentTarget.value = String(cfg.min);
                    }}
                  />
                  <Slider
                    value={[cfg.value]}
                    min={cfg.min}
                    max={cfg.max}
                    step={cfg.step ?? 0.1}
                    onValueChange={(vals) => handleVariableChange(name, vals[0])}
                    onPointerDown={() => hasMultiple && onParamSliderActiveChange?.(true)}
                    onPointerUp={() => hasMultiple && onParamSliderActiveChange?.(false)}
                    onPointerCancel={() => hasMultiple && onParamSliderActiveChange?.(false)}
                    onPointerLeave={() => hasMultiple && onParamSliderActiveChange?.(false)}
                    className="w-full"
                  />
                  <Input
                    type="text"
                    inputMode="decimal"
                    aria-label={`${name} max`}
                    className="w-20 h-8 px-2 py-1 text-xs"
                    defaultValue={cfg.max}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) handleVariableRangeChange(name, 'max', val);
                    }}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) handleVariableRangeChange(name, 'max', val);
                      else e.currentTarget.value = String(cfg.max);
                    }}
                  />
                  <span className="w-12 text-right text-xs text-muted-foreground tabular-nums" aria-live="polite">{cfg.value}</span>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
};

export default FormulaEditor;
